import {
    AsDisabled,
    LimitationProps,
    AsRestricted,
    Restricted,
    AsAllowed
} from '~/components/Builder/Common/CreationLimitations/creationLimitations.types';
import i18next from 'i18next';
import isEmpty from 'lodash/isEmpty';
import { calculatePageOrder } from '~/utils/page.utils';

export const forbidTwoInvoiceBlocks = ({
    targetFlow
}: LimitationProps<'targetFlow'>) => {
    const t = i18next.getFixedT(null, 'builder', 'creation_limitation_dialog');

    if (targetFlow.hasInvoiceBlock) {
        return AsDisabled(t('invoice_already_exists'));
    }

    return AsAllowed;
};

export interface ForbidInvoiceBeforeServiceSelectionRestriction
    extends Restricted {
    issueType: 'forbidInvoiceBeforeServiceSelection';
    solutionData: {
        newPageOrder: number;
        blockType: 'invoice_container_block';
        compositionType: undefined;
        newPageName: 'Invoice';
    };
}

export const forbidInvoiceBeforeServiceSelection = ({
    targetFlow,
    targetPage
}: LimitationProps<'targetFlow' | 'targetPage'>) => {
    const t = i18next.getFixedT(null, 'builder', 'creation_limitation_dialog');

    const pageOrder = targetPage?.order ?? Infinity; // if no page is provided - that means it's a new page.

    const { pagesWithServiceSelection } = targetFlow;
    const sortedServiceSelectionPages = pagesWithServiceSelection.sort(
        (a, b) => a.order - b.order
    );

    // No invoice before service selection
    if (
        !isEmpty(sortedServiceSelectionPages) &&
        pageOrder <=
            sortedServiceSelectionPages[sortedServiceSelectionPages.length - 1]
                .order
    ) {
        const newPageOrder = calculatePageOrder({
            pages: targetFlow.pages,
            relativeToPage:
                sortedServiceSelectionPages[
                    sortedServiceSelectionPages.length - 1
                ],
            shouldAddBeforePage: false
        });

        return AsRestricted<ForbidInvoiceBeforeServiceSelectionRestriction>({
            issueType: 'forbidInvoiceBeforeServiceSelection',
            reason: {
                short: 'Add invoice after service block?',
                long: `You can’t add an invoice to this page since it would come before your service block.
                But we can add it to a new page after your service blocks for you.`,
                cta: t('default_approve_cta')
            },
            solutionData: {
                newPageOrder,
                blockType: 'invoice_container_block',
                compositionType: undefined,
                newPageName: 'Invoice'
            }
        });
    }

    return AsAllowed;
};

export const forbidClientPaymentPlanInLeadForm = ({
    targetFlow,
    sourceBlock
}: LimitationProps<'targetFlow', 'sourceBlock'>) => {
    const t = i18next.getFixedT(null, 'builder', 'creation_limitation_dialog');

    if (
        targetFlow.isLeadForm &&
        sourceBlock &&
        sourceBlock.type === 'invoice_container_block' &&
        sourceBlock.isClientPaymentPlan
    ) {
        return AsDisabled(t('no_client_payment_plan_in_lead_form'));
    }

    return AsAllowed;
};
